import { useMutation } from "@apollo/client";
import {
  Button,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import {
  AdminCreateTagMutation,
  AdminCreateTagMutationVariables,
  TagFragment,
} from "gql/graphql";
import { useTags } from "modules/providers/tags-provider";
import React, { memo, useCallback, useMemo, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import colors from "app/colors";
import { HASH_KEY, SERVER_ERROR } from "modules/utils/Constants";
import {
  getMessageFromError,
  sortByName,
  stopPropagate,
} from "modules/utils/Functions";
import ADMIN_CREATE_TAG_MUTATION from "./_networking/admin-create-tag";
import { useOverlay } from "modules/providers/overlay-provider";
import AdminCreateTagSelect, {
  INewTagOption,
} from "./components/admin-create-tag-select";

interface IProps {
  onSaveCallback?: Function;
}

const AdminCreateTagModal = ({ onSaveCallback }: IProps) => {
  const { closeModal } = useOverlay();
  const { tagCategories, parentTags } = useTags();

  const {
    handleSubmit,
    formState: { errors },
    register,
    watch,
    clearErrors,
  } = useForm({
    mode: "onSubmit",
  });

  const parentTagId = watch("parentTagId");
  const tagCategoryId = watch("tagCategoryId");

  const isTagNameDisabled = !tagCategoryId && !parentTagId;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [value, setValue] = useState<readonly INewTagOption[]>([]);

  const [adminCreateTagMutationAPI] = useMutation<
    AdminCreateTagMutation,
    AdminCreateTagMutationVariables
  >(ADMIN_CREATE_TAG_MUTATION);

  const duplicateTags = useMemo(
    () => value.filter((v) => !!v.duplicateTag).map((v) => v.duplicateTag!),
    [value],
  );

  const hasDuplicate = duplicateTags.length > 0;

  const onSubmit = useCallback(
    async ({ parentTagId, tagCategoryId }: FieldValues) => {
      if (value.length === 0) {
        return;
      }

      setIsSubmitting(true);
      const results = await Promise.all(
        value.map((name) =>
          adminCreateTagMutationAPI({
            variables: {
              input: {
                name: name.value,
                parentTagId: parentTagId || null,
                tagCategoryId: tagCategoryId || null,
              },
            },
          }),
        ),
      );
      setIsSubmitting(false);
      const createdTags: TagFragment[] = results
        .map((result) => result.data?.adminCreateTag)
        .filter((tag): tag is TagFragment => !!tag);
      if (onSaveCallback) {
        onSaveCallback(createdTags);
      }
      closeModal();
    },
    [adminCreateTagMutationAPI, closeModal, onSaveCallback, value],
  );

  return (
    <Modal isOpen onClose={closeModal} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius={16}>
        <ModalHeader>
          <Text fontWeight={600} fontSize={24} textAlign="center">
            Create New Tags
          </Text>
        </ModalHeader>
        <ModalBody>
          <form
            id="create-tag-form"
            onSubmit={stopPropagate(handleSubmit(onSubmit))}
          >
            <Stack p={4} spacing={4}>
              <Stack
                spacing={4}
                bg={colors.whiteTertiary}
                p={3}
                borderRadius={8}
              >
                <FormControl isInvalid={!!errors?.tagCategoryId}>
                  <FormLabel fontSize="sm">Category </FormLabel>
                  <Select
                    isDisabled={parentTagId}
                    placeholder="Select Tag Category"
                    bg="white"
                    {...register("tagCategoryId", {
                      required: {
                        message:
                          "Either a Parent Tag or a Category is required",
                        value: !parentTagId,
                      },
                      deps: ["parentTagId"],
                    })}
                  >
                    {[...tagCategories].sort(sortByName).map((tagCategory) => (
                      <option key={tagCategory.id} value={tagCategory.id}>
                        {tagCategory.name}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>
                    {getMessageFromError(errors.tagCategoryId?.message)}
                  </FormErrorMessage>
                </FormControl>
                <Flex align="center">
                  <Divider borderColor={colors.gray30} />
                  <Text
                    px={3}
                    fontSize={14}
                    color={colors.grayMain}
                    fontWeight={500}
                  >
                    OR
                  </Text>
                  <Divider borderColor={colors.gray30} />
                </Flex>
                <FormControl isInvalid={!!errors?.parentTagId}>
                  <FormLabel>
                    <HStack>
                      <Text fontSize="sm">Parent Tag</Text>
                      <Text fontSize="xs" color={colors.gray30}>
                        (optional)
                      </Text>
                    </HStack>
                  </FormLabel>
                  <Select
                    placeholder="Select Parent Tag"
                    isDisabled={tagCategoryId}
                    bg="white"
                    {...register("parentTagId", {
                      required: {
                        message:
                          "Either a Parent Tag or a Category is required",
                        value: !tagCategoryId,
                      },
                      deps: ["tagCategoryId"],
                    })}
                  >
                    {parentTags.sort(sortByName).map((parentTag) => (
                      <option key={parentTag.id} value={parentTag.id}>
                        {HASH_KEY}
                        {parentTag.name}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>
                    {getMessageFromError(errors.parentTagId?.message)}
                  </FormErrorMessage>
                </FormControl>
              </Stack>
              <FormControl isInvalid={!!errors?.name}>
                <FormLabel fontSize="sm">Tag Name</FormLabel>
                <AdminCreateTagSelect
                  isDisabled={isTagNameDisabled}
                  onChange={setValue}
                  parentTagId={parentTagId}
                />
                <FormErrorMessage>
                  {getMessageFromError(errors?.name?.message)}
                </FormErrorMessage>
              </FormControl>
              {hasDuplicate && (
                <Text color={colors.red}>Duplicate tags found</Text>
              )}
              {errors.SERVER_ERROR && (
                <Text color={colors.red}>
                  {getMessageFromError(errors?.SERVER_ERROR?.message)}
                </Text>
              )}{" "}
              <HStack w="full" justifyContent="center">
                <Button
                  variant="cancel"
                  height={8}
                  w={28}
                  onClick={(e) => {
                    e.stopPropagation();
                    closeModal();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  height={8}
                  w={28}
                  onClick={() => {
                    clearErrors([SERVER_ERROR]);
                  }}
                  isDisabled={value.length === 0 || hasDuplicate}
                  type="submit"
                >
                  {isSubmitting ? <Spinner /> : "Save"}
                </Button>
              </HStack>
            </Stack>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default memo(AdminCreateTagModal);
